import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  useFollowUpReportDetail,
  useFollowUpReportTransitions,
  FOLLOW_UP_REPORT_STATUS_LABELS,
} from '@/hooks/useFollowUp';
import {
  FOLLOW_UP_PERMISSIONS,
  canSeeFollowUpReportsMenu,
  hasPermission,
} from '@/lib/followUpPermissions';

export default function FollowUpReportDetailPage() {
  const params = useParams<{ id: string }>();
  const id = params.id;
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { report, reviews, loading, error, fetchReport, setReport } =
    useFollowUpReportDetail();
  const transitions = useFollowUpReportTransitions();
  const [comment, setComment] = useState('');
  const [showRequestChanges, setShowRequestChanges] = useState(false);
  const [requestChangesKey, setRequestChangesKey] = useState(0);

  const canRead = canSeeFollowUpReportsMenu(permissions);
  const canUpdate = hasPermission(permissions, FOLLOW_UP_PERMISSIONS.UPDATE);
  const canReview = hasPermission(permissions, FOLLOW_UP_PERMISSIONS.REVIEW);
  const canPublish = hasPermission(permissions, FOLLOW_UP_PERMISSIONS.PUBLISH);

  useEffect(() => {
    if (!canRead || !id) return;
    void fetchReport(id);
  }, [id, canRead, fetchReport]);

  if (!canRead) return <AccessDenied resource="随访报告" />;
  if (loading || !report) {
    return <div className="p-6 text-sm text-muted-foreground">加载中…</div>;
  }

  const submit = async () => {
    const r = await transitions.submit(report.id, {
      expectedVersion: report.version,
    });
    if (r) setReport(r);
  };
  const approve = async () => {
    const r = await transitions.approve(report.id, {
      expectedVersion: report.version,
      reviewerComment: comment.trim() || undefined,
    });
    if (r) {
      setReport(r);
      setComment('');
    }
  };
  const requestChanges = async () => {
    const r = await transitions.requestChanges(report.id, {
      expectedVersion: report.version,
      reviewerComment: comment.trim(),
    });
    if (r) {
      setReport(r);
      setComment('');
      setShowRequestChanges(false);
    }
  };
  const publish = async () => {
    const r = await transitions.publish(report.id, {
      expectedVersion: report.version,
    });
    if (r) setReport(r);
  };
  const archive = async () => {
    const r = await transitions.archive(report.id, {
      expectedVersion: report.version,
    });
    if (r) setReport(r);
  };

  return (
    <div className="space-y-4 p-6">
      <Button variant="ghost" onClick={() => navigate('/follow-up/reports')}>
        ← 返回随访报告列表
      </Button>
      <Card className="space-y-2 p-4">
        <h1 className="text-xl font-semibold">{report.title}</h1>
        <div className="text-sm text-muted-foreground">
          客户 {report.customerId} · 状态{' '}
          {FOLLOW_UP_REPORT_STATUS_LABELS[report.status]} · 客户可见{' '}
          {report.customerVisible ? '是' : '否'}
        </div>
        <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
          {report.planId && (
            <div>
              <span className="text-muted-foreground">关联计划：</span>
              {report.planId}
            </div>
          )}
          {report.serviceUserId && (
            <div>
              <span className="text-muted-foreground">服务用户：</span>
              {report.serviceUserId}
            </div>
          )}
          {report.periodStart && (
            <div>
              <span className="text-muted-foreground">起：</span>
              {new Date(report.periodStart).toLocaleDateString()}
            </div>
          )}
          {report.periodEnd && (
            <div>
              <span className="text-muted-foreground">止：</span>
              {new Date(report.periodEnd).toLocaleDateString()}
            </div>
          )}
        </div>
        {report.summary && (
          <div className="text-sm">
            <span className="text-muted-foreground">摘要：</span>
            {report.summary}
          </div>
        )}
        {report.reviewerComment && (
          <div className="text-sm">
            <span className="text-muted-foreground">最近审核意见：</span>
            {report.reviewerComment}
          </div>
        )}
      </Card>

      <Card className="space-y-2 p-4">
        <h2 className="text-base font-semibold">报告内容</h2>
        <pre className="max-h-96 overflow-auto rounded-md border bg-muted/30 p-3 text-xs">
          {report.content}
        </pre>
      </Card>

      {error && (
        <Card className="border-red-300 bg-red-50 p-4 text-sm text-red-800">
          {error.message}
        </Card>
      )}
      {transitions.error && (
        <Card className="border-red-300 bg-red-50 p-4 text-sm text-red-800">
          {transitions.error.message}
        </Card>
      )}

      <Card className="space-y-3 p-4">
        <h2 className="text-base font-semibold">状态操作</h2>
        <div className="flex flex-wrap gap-2">
          {canUpdate &&
            (report.status === 'draft' ||
              report.status === 'changes_requested') && (
              <Button onClick={submit} disabled={transitions.loading}>
                提交审核
              </Button>
            )}
          {canReview && report.status === 'submitted' && (
            <>
              <Button onClick={approve} disabled={transitions.loading}>
                审核通过
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setRequestChangesKey((k) => k + 1);
                  setShowRequestChanges(true);
                }}
              >
                打回修改
              </Button>
            </>
          )}
          {canPublish && report.status === 'approved' && (
            <Button onClick={publish} disabled={transitions.loading}>
              发布给客户
            </Button>
          )}
          {canPublish &&
            (report.status === 'approved' || report.status === 'published') && (
              <Button
                variant="outline"
                onClick={archive}
                disabled={transitions.loading}
              >
                归档（撤回客户可见）
              </Button>
            )}
        </div>
        {showRequestChanges && canReview && (
          <div
            key={requestChangesKey}
            className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3"
          >
            <Label htmlFor="r-comment">审核意见（必填）</Label>
            <textarea
              id="r-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={2000}
              className="w-full rounded-md border bg-background p-2 text-sm"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowRequestChanges(false)}
              >
                收起
              </Button>
              <Button
                onClick={requestChanges}
                disabled={!comment.trim() || transitions.loading}
              >
                确认打回
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="space-y-2 p-4">
        <h2 className="text-base font-semibold">审核历史</h2>
        {reviews.length === 0 && (
          <div className="text-sm text-muted-foreground">暂无审核记录。</div>
        )}
        {reviews.length > 0 && (
          <ul className="space-y-2 text-sm">
            {reviews.map((r) => (
              <li key={r.id} className="rounded-md border bg-muted/20 p-2">
                <div className="text-xs text-muted-foreground">
                  {new Date(r.reviewedAt).toLocaleString()} · {r.reviewedBy}
                </div>
                <div>
                  {FOLLOW_UP_REPORT_STATUS_LABELS[r.fromStatus]} →{' '}
                  {FOLLOW_UP_REPORT_STATUS_LABELS[r.toStatus]} ·{' '}
                  {r.decision}
                </div>
                {r.reviewerComment && (
                  <div className="text-xs text-muted-foreground">
                    {r.reviewerComment}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
